import { computed, ref, ComputedRef, Ref } from 'vue';
import { disclaimerService } from '@/services/v2/DisclaimerService';
import { Announcement } from '@/types/Announcement';

interface AnnouncementsState {
  announcements: Ref<Announcement[]>;
  activeAnnouncements: ComputedRef<Announcement[]>;
  dismissedAnnouncements: ComputedRef<Announcement[]>;
  load: () => Promise<void>;
  dismiss: (announcement: Announcement) => Promise<void>;
}

// Singleton state shared by the banner stack and the header button, so the two stay in
// sync off a single fetch.
const announcements = ref<Announcement[]>([]);

const activeAnnouncements = computed(() => announcements.value.filter((a) => !a.dismissed));
const dismissedAnnouncements = computed(() => announcements.value.filter((a) => a.dismissed));

export const useAnnouncements = (): AnnouncementsState => {
  const load = async (): Promise<void> => {
    try {
      const result = await disclaimerService.getActiveAnnouncements();
      // axios resolves (not rejects) a request the browser cancelled mid-navigation, with
      // status 0 and an empty-string body; assigning that would break every computed here.
      if (Array.isArray(result)) {
        announcements.value = result;
      }
    } catch (e) {
      console.error('failed to load announcements', e);
    }
  };

  const dismiss = async (announcement: Announcement): Promise<void> => {
    announcements.value = announcements.value.map((a) => (a.id === announcement.id ? { ...a, dismissed: true } : a));
    try {
      await disclaimerService.acceptDisclaimer(announcement.id);
    } catch (e) {
      console.error('failed to record announcement acknowledgement', e);
    }
  };

  return { announcements, activeAnnouncements, dismissedAnnouncements, load, dismiss };
};
